import { DEFAULT_BIRTHDAYS, sanitizeBirthday, type Birthday, type Person, type PetId } from '../core/state';
import { loadGame } from '../core/save';
import { clock, m, onLangChange, type Messages } from '../i18n';
import { sfx, unlockAudio } from '../audio/sfx';
import { drawCharacter, PLAYER_LOOKS } from '../render/sprites';
import { PET_EMOJI } from '../render/renderer';
import { h, $ } from './dom';
import { layers } from './layers';
import { langSelect } from './menu';
import { say } from './dialog';
import { openSaveCode } from './lifeSave';

export interface StartChoice {
  look: number;
  pet: PetId;
  birthdays: Record<Person, Birthday>;
}

interface Handlers {
  onNew(c: StartChoice): void;
  onContinue(): void;
}

const choice: StartChoice = { look: 0, pet: 'rabbit', birthdays: structuredClone(DEFAULT_BIRTHDAYS) };
let handlers: Handlers;
/** School grade (3–6) for a new game; applied by the life package on the first dayStart. */
let grade = 4;
let pendingGrade: number | null = null;
export function takeNewGameGrade(): number | null {
  const g = pendingGrade;
  pendingGrade = null;
  return g;
}
let raf = 0;

export function showStart(h0: Handlers): void {
  handlers = h0;
  layers.open('start');
  $('start').classList.add('show');
  render();
  animate();
}

export function hideStart(): void {
  $('start').classList.remove('show');
  layers.close('start');
  cancelAnimationFrame(raf);
}

onLangChange(() => {
  if (layers.isOpen('start')) render();
});

function render(): void {
  const t = m().start;
  const save = loadGame();
  const dayName = (k: keyof Messages['days']) => m().days[k].name;
  const days = ['fri', 'sat', 'sun'] as const;

  const looks = PLAYER_LOOKS.map((_, i) =>
    h(
      'button',
      {
        class: `look${choice.look === i ? ' on' : ''}`,
        'aria-label': m().looks[i],
        title: m().looks[i],
        onclick: () => {
          choice.look = i;
          sfx('pop');
          render();
        },
      },
      h('canvas', { width: 64, height: 80, 'data-look': String(i) }),
    ),
  );

  const pets = (Object.keys(PET_EMOJI) as PetId[]).map((p) =>
    h(
      'button',
      {
        class: `chip${choice.pet === p ? ' on' : ''}`,
        onclick: () => {
          choice.pet = p;
          sfx('pop');
          render();
        },
      },
      `${PET_EMOJI[p]} ${m().pets[p]}`,
    ),
  );

  const bday = (p: Person, label: string) => {
    const b = choice.birthdays[p];
    const num = (key: 'm' | 'd', max: number) =>
      h('input', {
        type: 'number',
        inputmode: 'numeric',
        min: 1,
        max,
        value: String(b[key]),
        'aria-label': `${label} ${key === 'm' ? t.month : t.day}`,
        onchange: (e: Event) => {
          const v = Number((e.target as HTMLInputElement).value);
          choice.birthdays[p] = sanitizeBirthday({ ...choice.birthdays[p], [key]: v }, DEFAULT_BIRTHDAYS[p]);
          render();
        },
      });
    return h('label', { class: 'bday' }, h('span', null, label), h('span', { class: 'bday-in' }, num('m', 12), t.month, num('d', 31), t.day));
  };

  $('start').replaceChildren(
    h(
      'div',
      { class: 'start-card' },
      h('div', { class: 'logo' }, h('span', null, '🏫'), h('h1', null, t.title), h('p', { class: 'tag' }, t.tagline)),
      h('p', { class: 'blurb' }, t.blurb),
      h('h4', null, `🌐 ${t.language}`),
      langSelect(),
      save
        ? h(
            'button',
            { class: 'btn gold big', onclick: () => (unlockAudio(), sfx('ok'), handlers.onContinue()) },
            `▶ ${t.continue}`,
            h('small', null, t.continueInfo(save.week, dayName(days[save.dayIndex % 3]), clock(save.time))),
          )
        : null,
      h('details', { class: 'setup', open: !save },
        h('summary', null, `✨ ${t.newGame}`),
        h('h4', null, t.look),
        h('div', { class: 'looks' }, looks),
        h('h4', null, t.pet),
        h('div', { class: 'chips' }, pets),
        h('h4', null, m().life.grade.title),
        h(
          'div',
          { class: 'chips life-grade' },
          m().life.grade.names.map((name, i) =>
            h('button', { class: `chip${grade === i + 3 ? ' on' : ''}`, onclick: () => ((grade = i + 3), sfx('pop'), render()) }, name),
          ),
        ),
        h('p', { class: 'hint' }, m().life.grade.hint),
        h('h4', null, `🎂 ${t.birthdays}`),
        h('div', { class: 'bdays' }, bday('me', t.you), bday('mom', t.mom), bday('dad', t.dad), bday('emma', t.emma)),
        h(
          'button',
          {
            class: `btn ${save ? 'ghost' : 'gold'} big`,
            onclick: () => {
              unlockAudio();
              const go = () => (sfx('fanfare'), (pendingGrade = grade), handlers.onNew(structuredClone(choice)));
              if (!save) return go();
              say({ who: t.newGame, icon: '⚠️', text: t.confirmNew, choices: [{ text: m().common.yes, run: go }, { text: m().common.no }] });
            },
          },
          `✨ ${t.newGame}`,
        ),
      ),
      h('button', { class: 'btn ghost small life-start-code', onclick: () => (sfx('click'), openSaveCode(true)) }, m().life.save.startButton),
      h('p', { class: 'controls-hint' }, t.controls),
    ),
  );
}

/** The look previews walk in place on the start screen. */
function animate(): void {
  cancelAnimationFrame(raf);
  const loop = (now: number) => {
    if (!layers.isOpen('start')) return;
    document.querySelectorAll<HTMLCanvasElement>('#start canvas[data-look]').forEach((cv) => {
      const i = Number(cv.dataset.look);
      const c = cv.getContext('2d')!;
      c.setTransform(1, 0, 0, 1, 0, 0);
      c.clearRect(0, 0, cv.width, cv.height);
      c.imageSmoothingEnabled = false;
      c.setTransform(2, 0, 0, 2, 0, 0);
      const on = i === choice.look;
      const phase = on ? (now / 600) % 1 : 0;
      drawCharacter(c, PLAYER_LOOKS[i], 16, 36, { facing: 'down', phase, moving: on, blink: (now / 1000) % 4 < 0.12, breath: (now / 1600) % 1 });
    });
    raf = requestAnimationFrame(loop);
  };
  raf = requestAnimationFrame(loop);
}
